import React from 'react';
import { useFormikContext, getIn } from 'formik';
import Select, { MultiValue } from 'react-select';

export interface Option {
  value: string;
  label: string;
}

interface ComboBoxMultiProps {
  // Props obrigatórias
  options: Option[];

  // Props opcionais para standalone
  value?: string[];
  onChange?: (values: string[]) => void;

  // Props opcionais para Formik  
  name?: string;

  // Props gerais
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  error?: string;

  // Props do react-select
  isSearchable?: boolean;
  isClearable?: boolean;
  closeMenuOnSelect?: boolean;
}

const ComboBoxMulti: React.FC<ComboBoxMultiProps> = ({
  options,
  value: externalValue,
  onChange: externalOnChange,
  name,
  label,
  placeholder = "Selecione...",
  disabled = false,
  required = false,
  className = "",
  error: externalError,
  isSearchable = true,
  isClearable = true,
  closeMenuOnSelect = false,
}) => {
  // ✅ Verificar se está sendo usado com Formik
  const formikContext = useFormikContext();
  const isFormikMode = formikContext && name;

  // ✅ Valores e funções do Formik (se disponível)
  let formikValue: string[] = [];
  let formikError: string = '';
  let formikTouched: boolean = false;
  let setFieldValue: ((field: string, value: string[]) => void) | undefined;

  if (isFormikMode) {
    const { values, errors, touched, setFieldValue: formikSetFieldValue } = formikContext;
    formikValue = getIn(values, name!) || [];
    formikError = getIn(errors, name!);
    formikTouched = getIn(touched, name!);
    setFieldValue = formikSetFieldValue;
  }

  // ✅ Determinar valores atuais
  const currentValue = isFormikMode ? formikValue : (externalValue || []);
  const currentError = isFormikMode ? (formikTouched ? formikError : '') : (externalError || '');

  // ✅ Filtrar opções válidas (remover vazias)
  const validOptions = options.filter(option => option.value && option.value.trim() !== '');

  // ✅ Converter string[] para Option[]
  const getSelectedOptions = (): Option[] => {
    if (!Array.isArray(currentValue)) return [];
    return validOptions.filter(option => currentValue.includes(option.value));
  };

  // ✅ Handler de mudança
  const handleChange = (selectedOptions: MultiValue<Option>) => {
    const selectedValues = selectedOptions ? selectedOptions.map(option => option.value) : [];

    if (isFormikMode && setFieldValue) {
      setFieldValue(name!, selectedValues);
    }

    if (externalOnChange) {
      externalOnChange(selectedValues);
    }
  };

  // ✅ Estilos condicionais
  const hasError = !!currentError;

  return (
    <div className={`mb-2 ${className}`}>
      {/* ✅ Label similar ao ComboBoxFormik */}
      {label && (
        <label htmlFor={name} className="block font-medium text-[16px] text-gray-600">
          {label} &nbsp; {required ? <b className="text-redPrimary">*</b> : ' '}
        </label>
      )}

      {/* ✅ Select com estilos similares ao ComboBoxFormik */}
      <Select<Option, true>
        isMulti
        name={name}
        options={validOptions} // ✅ Usar opções filtradas
        value={getSelectedOptions()}
        onChange={handleChange}
        isDisabled={disabled}
        isSearchable={isSearchable}
        isClearable={isClearable}
        closeMenuOnSelect={closeMenuOnSelect}
        placeholder={placeholder}
        className="react-select-container"
        classNamePrefix="react-select"
        // ✅ Estilos inline similares ao select nativo
        styles={{
          control: (base) => ({
            ...base,
            border: hasError ? '1px solid #ef4444' : '1px solid #d1d5db',
            borderRadius: '0.375rem', // rounded
            padding: '0.5rem', // p-2 equivalent
            minHeight: '42px',
            backgroundColor: disabled ? '#f9fafb' : '#ffffff',
            boxShadow: 'none',
            '&:hover': {
              border: hasError ? '1px solid #ef4444' : '1px solid #9ca3af',
            },
          }),
          valueContainer: (base) => ({
            ...base,
            padding: '0',
          }),
          input: (base) => ({
            ...base,
            margin: '0',
            paddingTop: '0',
            paddingBottom: '0',
          }),
          indicatorSeparator: () => ({
            display: 'none',
          }),
          dropdownIndicator: (base, state) => ({
            ...base,
            color: '#6b7280',
            '&:hover': {
              color: '#374151',
            },
            transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }),
          menu: (base) => ({
            ...base,
            border: '1px solid #d1d5db',
            borderRadius: '0.375rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            zIndex: 50,
          }),
          menuList: (base) => ({
            ...base,
            padding: '0.25rem',
            maxHeight: '240px',
          }),
          option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected
              ? '#3b82f6'
              : state.isFocused
                ? '#eff6ff'
                : 'transparent',
            color: state.isSelected ? 'white' : '#374151',
            padding: '0.5rem 0.75rem',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: state.isSelected ? '#3b82f6' : '#eff6ff',
            },
          }),
          multiValue: (base) => ({
            ...base,
            backgroundColor: '#f3f4f6',
            border: '1px solid #d1d5db',
            borderRadius: '0.25rem',
            margin: '0.125rem',
          }),
          multiValueLabel: (base) => ({
            ...base,
            color: '#374151',
            fontSize: '14px',
            padding: '0.25rem 0.5rem',
          }),
          multiValueRemove: (base) => ({
            ...base,
            color: '#6b7280',
            borderRadius: '0 0.25rem 0.25rem 0',
            '&:hover': {
              backgroundColor: '#ef4444',
              color: 'white',
            },
          }),
          placeholder: (base) => ({
            ...base,
            color: '#9ca3af',
            fontSize: '14px',
          }),
          noOptionsMessage: (base) => ({
            ...base,
            color: '#6b7280',
            fontSize: '14px',
            padding: '0.5rem 0.75rem',
          }),
        }}
      />

      {/* ✅ Mensagem de erro similar ao ComboBoxFormik */}
      {currentError && (
        <small className="text-redPrimary/90" aria-live="polite">
          {currentError}
        </small>
      )}
    </div>
  );
};

export default ComboBoxMulti;